import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CloudUpload, Image } from 'lucide-react'
import { apiURL } from '../../../utilities/URLs'
import { account } from '../../../classes/Account'
import { refreshAccessToken } from '../../../utilities/extensions'

export default function IDVerificationPage() {
  const { applicationId } = useParams()
  const navigate = useNavigate()

  const [idPhoto, setIdPhoto] = useState(null)
  const [selfie, setSelfie] = useState(null)
  const [idPhotoPreview, setIdPhotoPreview] = useState(null)
  const [selfiePreview, setSelfiePreview] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  const handleIdPhotoSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      setIdPhoto(file)
      setError('')

      const reader = new FileReader()
      reader.onloadend = () => {
        setIdPhotoPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSelfieSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      setSelfie(file)
      setError('')

      const reader = new FileReader()
      reader.onloadend = () => {
        setSelfiePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUpload = async () => {
    setUploading(true)
    setError('')

    const formData = new FormData()
    formData.append('id', idPhoto)
    formData.append('selfie', selfie)

    const uploadVerificationResponse = await fetch(`${apiURL}/api/applications/${applicationId}/id-verification`, {
      method: 'POST',
      headers: {
        'Authorization': account.accessToken
      },
      body: formData
    })

    const uploadVerificationData = await uploadVerificationResponse.json()
    if (uploadVerificationData.response.error) {
      if (uploadVerificationData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        if (refreshedToken && refreshedToken.length > 0)
          await handleUpload()
        else
          navigate('/', { state: { expired: true } })
      } else
        setError(uploadVerificationData.response.error)
    } else
      navigate(`/applications/${applicationId}/business-verification-upload`)
  }

  return (
    <div className='w-full px-4 sm:px-8 flex flex-col items-center py-8 text-slate-900 dark:text-white transition-colors duration-300'>
      <div className='flex flex-col items-center w-full space-y-6'>
        <h1 className='text-3xl font-bold text-slate-900 dark:text-white text-center'>Identity Verification</h1>
        <p className='text-center text-gray-600 dark:text-slate-300'>Please upload a clear photo of your government-issued ID and a selfie for verification purposes.</p>
        <div className='w-full bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 space-y-6 transition-colors duration-300'>
          <div className='space-y-3'>
            <h2 className='text-lg font-semibold text-slate-900 dark:text-white'>Government-Issued ID</h2>
            <label htmlFor='id-photo-upload' className='w-full border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl p-8 flex flex-col items-center justify-center text-center bg-gray-50 hover:bg-gray-100 dark:bg-slate-700/50 dark:hover:bg-slate-700 transition cursor-pointer'>
              <CloudUpload size={40} className='text-gray-400 dark:text-slate-400 mb-2' />
              <p className='text-md font-semibold text-gray-700 dark:text-slate-200 mb-1'>
                {idPhoto ? 'Change ID Photo' : 'Upload ID Photo'}
              </p>
              <p className='text-sm text-gray-500 dark:text-slate-400'>Click to select an image file</p>
            </label>
            <input
              id='id-photo-upload'
              type='file'
              accept='image/*'
              className='hidden'
              onChange={handleIdPhotoSelect}
            />
            {idPhotoPreview && (
              <div className='flex items-center space-x-4 p-4 bg-gray-50 dark:bg-slate-700 rounded-lg'>
                <Image size={24} className='text-ertc-primary dark:text-blue-400' />
                <div className='flex-1'>
                  <p className='text-sm font-medium text-gray-700 dark:text-slate-200'>{idPhoto.name}</p>
                  <p className='text-xs text-gray-500 dark:text-slate-400'>
                    {(idPhoto.size / 1024).toFixed(2)} KB
                  </p>
                </div>
                <img
                  src={idPhotoPreview}
                  alt='ID Preview'
                  className='h-20 w-20 object-cover rounded border border-gray-300 dark:border-slate-600'
                />
              </div>
            )}
          </div>
          <div className='space-y-3'>
            <h2 className='text-lg font-semibold text-slate-900 dark:text-white'>Selfie Photo</h2>

            <label htmlFor='selfie-upload' className='w-full border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl p-8 flex flex-col items-center justify-center text-center bg-gray-50 hover:bg-gray-100 dark:bg-slate-700/50 dark:hover:bg-slate-700 transition cursor-pointer'>
              <CloudUpload size={40} className='text-gray-400 dark:text-slate-400 mb-2' />
              <p className='text-md font-semibold text-gray-700 dark:text-slate-200 mb-1'>{selfie ? 'Change Selfie' : 'Upload Selfie'}</p>
              <p className='text-sm text-gray-500 dark:text-slate-400'>Click to select an image file</p>
            </label>
            <input
              id='selfie-upload'
              type='file'
              accept='image/*'
              className='hidden'
              onChange={handleSelfieSelect}
            />
            {selfiePreview && (
              <div className='flex items-center space-x-4 p-4 bg-gray-50 dark:bg-slate-700 rounded-lg'>
                <Image size={24} className='text-ertc-primary dark:text-blue-400' />
                <div className='flex-1'>
                  <p className='text-sm font-medium text-gray-700 dark:text-slate-200'>{selfie.name}</p>
                  <p className='text-xs text-gray-500 dark:text-slate-400'>
                    {(selfie.size / 1024).toFixed(2)} KB
                  </p>
                </div>
                <img
                  src={selfiePreview}
                  alt='Selfie Preview'
                  className='h-20 w-20 object-cover rounded border border-gray-300 dark:border-slate-600'
                />
              </div>
            )}
          </div>
        </div>
        {error && (
          <div className='w-full bg-red-50 dark:bg-red-900/10 border border-red-300 dark:border-red-500/20 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg'>
            {error}
          </div>
        )}
        <button
          onClick={handleUpload}
          disabled={!idPhoto || !selfie || uploading}
          className='px-10 py-3 bg-ertc-primary rounded-md shadow-md text-white text-lg font-semibold disabled:bg-gray-400 dark:disabled:bg-slate-600 disabled:cursor-not-allowed transition'
        >
          Upload
        </button>
        {uploading && (
          <div className='flex flex-row items-center justify-center gap-2 mt-5 transition-colors duration-300'>
            <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary h-8 w-8 animate-spin'></div>
            <p className='text-md text-center text-slate-600 dark:text-slate-300'>Uploading verification documents...</p>
          </div>
        )}
      </div>
    </div>
  )
}
